import {controlStyle} from '../lib/formStyles.js';
import Button from '../shared/Button.js';
// Auto-extracted by Phase 2 Round 2 (verbatim). See MIGRATION_PLAN §6.
import React from 'react';
import {
  recordFieldRowClass,
  recordFieldLabel,
  recordControl,
  recordTextarea,
  recordCheckbox,
} from '../shared/recordPageControls.jsx';

import Badge from '../shared/Badge.jsx';
import RuminantWeightHistory from './RuminantWeightHistory.js';
import {
  ruminantDetailCardStyle,
  ruminantDetailHeaderRowStyle,
  ruminantSectionHeaderStyle,
  RuminantNavigateBackBanner,
  RuminantPriorTagsSection,
} from './ruminantPresentation.tsx';

type CowRecord = Record<string, any>;
type CowDetailProps = {
  cow: CowRecord;
  weighIns?: CowRecord[];
  calving?: CowRecord[];
  comments?: CowRecord[];
  calves?: CowRecord[];
  dam?: CowRecord | null;
  cattleList?: CowRecord[];
  fmt: (...args: any[]) => any;
  HERDS?: readonly string[];
  HERD_LABELS: Record<string, string>;
  onEdit?: ((...args: any[]) => any) | null;
  onDelete?: ((...args: any[]) => any) | null;
  onComment?: ((...args: any[]) => any) | null;
  onEditComment?: ((...args: any[]) => any) | null;
  onDeleteComment?: ((...args: any[]) => any) | null;
  onAddCalving?: (...args: any[]) => any;
  onDeleteCalving?: (...args: any[]) => any;
  onNavigateToCow?: (cow: CowRecord) => void;
  onNavigateBack?: () => void;
  canNavigateBack?: boolean;
  backToTag?: string;
  onPatch?: (patch: CowRecord) => void;
  onTransfer?: (herd: string) => void;
  // Migration 221: transfer_cattle_animal admits admin, management and
  // farm_team only. A locked select still shows the herd but cannot change it
  // (and never falls back to a raw herd patch).
  transferLocked?: boolean;
  // Migration 222: only admin, management and farm_team may write cattle rows
  // directly. readOnly renders every inline editor disabled and hides the
  // prior-tag add/remove controls, so a read-only role is never offered an
  // edit the database refuses (Design Constitution P01).
  readOnly?: boolean;
  onClose?: () => void;
  originOpts?: CowRecord[];
  breedOpts?: CowRecord[];
  ageFieldLabel?: string;
  ageLabel?: string;
  hideComments?: boolean;
};

const CowDetail = ({
  cow,
  weighIns,
  calving,
  comments,
  calves,
  // dam / onEdit: still passed by the record page; the extracted weight
  // history no longer needs them here.
  dam: _dam,
  cattleList,
  fmt,
  HERDS,
  HERD_LABELS,
  onEdit: _onEdit,
  onDelete,
  onComment,
  onEditComment,
  onDeleteComment,
  onAddCalving,
  onDeleteCalving,
  onNavigateToCow,
  onNavigateBack,
  canNavigateBack,
  backToTag,
  onPatch,
  onTransfer,
  transferLocked = false,
  readOnly = false,
  onClose,
  originOpts,
  breedOpts,
  ageFieldLabel = 'Age',
  ageLabel,
  hideComments,
}: CowDetailProps) => {
  // Auto-save inline editor — uncontrolled inputs that fire onPatch on blur
  // when the value changed. Numbers parse to number; empty strings become null.
  const patchOnBlur = (field: string, parser: 'number' | 'text') => (ev: React.FocusEvent<HTMLInputElement>) => {
    if (!onPatch) return;
    const raw = ev.target.value;
    const cur = cow[field];
    let next;
    if (parser === 'number') {
      next = raw === '' ? null : parseFloat(raw);
      if (Number.isNaN(next)) next = null;
    } else {
      next = (raw || '').trim() || null;
    }
    const curNorm = cur === '' ? null : cur;
    if (String(next) === String(curNorm)) return;
    // Migration 245: the retag operation composes old_tags server-side from
    // the declared source; the page routes a tag patch to it.
    onPatch(field === 'tag' ? {tag: next} : {[field]: next});
  };
  const patchOnChange = (field: string) => (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!onPatch) return;
    const v = ev.target.type === 'checkbox' ? ev.target.checked : ev.target.value || null;
    onPatch({[field]: v});
  };
  const transferOnChange = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    if (transferLocked) return;
    const v = ev.target.value || null;
    if (!v || v === cow.herd) return;
    if (typeof onTransfer === 'function') {
      onTransfer(v);
      return;
    }
    if (onPatch) onPatch({herd: v});
  };
  // Editable old_tags helpers
  function patchOldTagAt(idx: number, field: string, value: any) {
    if (!onPatch) return;
    const next = (Array.isArray(cow.old_tags) ? cow.old_tags : []).slice();
    next[idx] = {...next[idx], [field]: value};
    onPatch({old_tags: next});
  }
  function addOldTag() {
    if (!onPatch) return;
    const next = [...(Array.isArray(cow.old_tags) ? cow.old_tags : []), {tag: '', changed_at: '', source: 'manual'}];
    onPatch({old_tags: next});
  }
  function removeOldTag(idx: number) {
    if (!onPatch) return;
    const next = (Array.isArray(cow.old_tags) ? cow.old_tags : []).filter((_, i) => i !== idx);
    onPatch({old_tags: next});
  }
  const editInp: React.CSSProperties = controlStyle;
  const [commentText, setCommentText] = React.useState('');
  const [showCalvingForm, setShowCalvingForm] = React.useState(false);
  const [editingCommentId, setEditingCommentId] = React.useState(null);
  const [editingCommentText, setEditingCommentText] = React.useState('');
  const [calvingForm, setCalvingForm] = React.useState({
    calving_date: new Date().toISOString().slice(0, 10),
    calf_tag: '',
    sire_tag: '',
    complications_flag: false,
    complications_desc: '',
    notes: '',
  });
  const sectionTitle = ruminantSectionHeaderStyle;
  const isMomma = cow.herd === 'mommas';
  async function submitCalving() {
    if (!onAddCalving) return;
    const ok = await onAddCalving(calvingForm);
    if (ok) {
      setShowCalvingForm(false);
      setCalvingForm({
        calving_date: new Date().toISOString().slice(0, 10),
        calf_tag: '',
        sire_tag: '',
        complications_flag: false,
        complications_desc: '',
        notes: '',
      });
    }
  }

  // Tag lookup helper for cross-cow navigation (calving → calf, lineage → dam/sire)
  const findByTag = (t: string): CowRecord | null =>
    t && Array.isArray(cattleList) ? cattleList.find((x: CowRecord) => x.tag === t) || null : null;
  const linkStyle: React.CSSProperties = {
    color: 'var(--brand)',
    cursor: 'pointer',
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
  };
  const TagLink = ({tag, prefix, label}: {tag: string; prefix?: string; label?: string}) => {
    const t = findByTag(tag);
    if (!t || !onNavigateToCow) return <span>{(prefix || '') + (label || '#' + tag)}</span>;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onNavigateToCow(t);
        }}
        style={linkStyle}
      >
        {(prefix || '') + (label || '#' + tag)}
      </button>
    );
  };
  return (
    <div data-cow-detail style={ruminantDetailCardStyle}>
      <RuminantNavigateBackBanner
        canNavigateBack={canNavigateBack}
        onNavigateBack={onNavigateBack}
        backToTag={backToTag}
        noun="cow"
        linkColor="var(--brand)"
      />
      {/* Header: editable tag + labeled herd/sex/breed selects + close X */}
      <div style={ruminantDetailHeaderRowStyle}>
        <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
          <span style={{fontSize: 16, fontWeight: 700, color: 'var(--text-primary)'}}>{'#'}</span>
          <input
            type="text"
            defaultValue={cow.tag || ''}
            disabled={readOnly}
            onBlur={patchOnBlur('tag', 'text')}
            placeholder="tag"
            style={{...editInp, width: 90, fontWeight: 700, fontSize: 14, color: 'var(--text-primary)'}}
          />
        </div>
        <div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--ink-muted)',
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            Herd
          </div>
          <select
            data-cattle-herd-status-select="1"
            defaultValue={cow.herd || ''}
            onChange={transferOnChange}
            disabled={transferLocked}
            style={{...editInp, width: 140}}
          >
            {(HERDS || []).map((h) => (
              <option key={h} value={h}>
                {HERD_LABELS[h] || h}
              </option>
            ))}
          </select>
        </div>
        <div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--ink-muted)',
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            Sex
          </div>
          <select
            defaultValue={cow.sex || ''}
            disabled={readOnly}
            onChange={patchOnChange('sex')}
            style={{...editInp, width: 100}}
          >
            <option value="">{'\u2014 sex \u2014'}</option>
            <option value="cow">Cow</option>
            <option value="heifer">Heifer</option>
            <option value="bull">Bull</option>
            <option value="steer">Steer</option>
            <option value="calf">Calf</option>
          </select>
        </div>
        <div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--ink-muted)',
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            Breed
          </div>
          <select
            defaultValue={cow.breed || ''}
            disabled={readOnly}
            onChange={patchOnChange('breed')}
            style={{...editInp, width: 210}}
          >
            <option value="">{'\u2014 breed \u2014'}</option>
            {(breedOpts || [])
              .filter((b) => b.active)
              .map((b) => (
                <option key={b.id} value={b.label}>
                  {b.label}
                </option>
              ))}
            {cow.breed && !(breedOpts || []).some((b) => b.active && b.label === cow.breed) && (
              <option value={cow.breed}>{cow.breed + ' (historical)'}</option>
            )}
          </select>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            title="Close"
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: '1px solid var(--border-strong)',
              borderRadius: 10,
              color: 'var(--ink-muted)',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              padding: '2px 10px',
              fontFamily: 'inherit',
            }}
          >
            {'\u00d7'}
          </button>
        )}
      </div>
      {/* Identity | Lineage: two columns on desktop; one column on phones (§7) so
          the Lineage inputs never sit off-screen at 390px. */}
      <div data-mobile-1col="1" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16}}>
        {/* Identity (editable) */}
        <div>
          <div style={sectionTitle}>Identity</div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--ink)',
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '4px 8px',
              alignItems: 'center',
            }}
          >
            <span style={{color: 'var(--ink-faint)'}}>Reg #</span>
            <input
              type="text"
              defaultValue={cow.registration_num || ''}
              disabled={readOnly}
              onBlur={patchOnBlur('registration_num', 'text')}
              style={editInp}
            />
            <span style={{color: 'var(--ink-faint)'}}>% Wagyu</span>
            <input
              type="number"
              min="0"
              max="100"
              defaultValue={cow.pct_wagyu == null ? '' : cow.pct_wagyu}
              disabled={readOnly}
              onBlur={patchOnBlur('pct_wagyu', 'number')}
              style={editInp}
            />
            <span style={{color: 'var(--ink-faint)'}}>Origin</span>
            <select
              defaultValue={cow.origin || ''}
              disabled={readOnly}
              onChange={patchOnChange('origin')}
              style={editInp}
            >
              <option value="">{'\u2014 select \u2014'}</option>
              {(originOpts || [])
                .filter((o) => o.active)
                .map((o) => (
                  <option key={o.id} value={o.label}>
                    {o.label}
                  </option>
                ))}
              {cow.origin && !(originOpts || []).some((o) => o.active && o.label === cow.origin) && (
                <option value={cow.origin}>{cow.origin}</option>
              )}
            </select>
            <span style={{color: 'var(--ink-faint)'}}>Birth</span>
            <input
              type="date"
              defaultValue={cow.birth_date || ''}
              disabled={readOnly}
              onBlur={patchOnBlur('birth_date', 'text')}
              style={editInp}
            />
            <span style={{color: 'var(--ink-faint)'}}>{ageFieldLabel}</span>
            <span
              style={{
                fontSize: 12,
                padding: '5px 8px',
                color: 'var(--ink-muted)',
                fontFamily: 'inherit',
              }}
            >
              {ageLabel || '—'}
            </span>
            <span style={{color: 'var(--ink-faint)'}}>Purchased</span>
            <input
              type="date"
              defaultValue={cow.purchase_date || ''}
              disabled={readOnly}
              onBlur={patchOnBlur('purchase_date', 'text')}
              style={editInp}
            />
            <span style={{color: 'var(--ink-faint)'}}>Purchase $</span>
            <input
              type="number"
              min="0"
              step="0.01"
              defaultValue={cow.purchase_amount == null ? '' : cow.purchase_amount}
              disabled={readOnly}
              onBlur={patchOnBlur('purchase_amount', 'number')}
              style={editInp}
            />
            {(cow.sex === 'cow' || cow.sex === 'heifer') && (
              <>
                <span style={{color: 'var(--ink-faint)'}}>Breeding</span>
                <select
                  defaultValue={cow.breeding_status || ''}
                  disabled={readOnly}
                  onChange={patchOnChange('breeding_status')}
                  style={editInp}
                >
                  <option value="">{'\u2014 not set \u2014'}</option>
                  <option value="OPEN">Open</option>
                  <option value="PREGNANT">Pregnant</option>
                  <option value="N/A">N/A</option>
                </select>
              </>
            )}
          </div>
        </div>
        {/* Lineage */}
        <div data-lineage-section="1">
          <div style={sectionTitle}>Lineage</div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--ink)',
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '4px 8px',
              alignItems: 'center',
            }}
          >
            <span style={{color: 'var(--ink-faint)'}}>Dam Tag #</span>
            <input
              type="text"
              defaultValue={cow.dam_tag || ''}
              disabled={readOnly}
              onBlur={patchOnBlur('dam_tag', 'text')}
              style={editInp}
            />
            {cow.dam_tag && findByTag(cow.dam_tag) && (
              <>
                <span style={{color: 'var(--ink-faint)'}}>{}</span>
                <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>
                  <TagLink tag={cow.dam_tag} prefix="View " />{' '}
                  {'(' + ((findByTag(cow.dam_tag) || {}).breed || '?') + ')'}
                </span>
              </>
            )}
            <span style={{color: 'var(--ink-faint)'}}>Sire Tag #</span>
            <input
              type="text"
              defaultValue={cow.sire_tag || ''}
              disabled={readOnly}
              onBlur={patchOnBlur('sire_tag', 'text')}
              style={editInp}
            />
            {cow.sire_tag && findByTag(cow.sire_tag) && (
              <>
                <span style={{color: 'var(--ink-faint)'}}>{}</span>
                <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>
                  <TagLink tag={cow.sire_tag} prefix="View " />{' '}
                  {'(' + ((findByTag(cow.sire_tag) || {}).breed || '?') + ')'}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Prior Tags editor */}
      <RuminantPriorTagsSection
        oldTags={cow.old_tags || []}
        readOnly={readOnly}
        editInp={editInp}
        onAdd={addOldTag}
        onPatchAt={patchOldTagAt}
        onRemove={removeOldTag}
        addColor="var(--brand)"
        addBorder="1px dashed var(--border)"
      />
      {/* Breeding blacklist (hidden for steers \u2014 they can't breed) */}
      {cow.sex !== 'steer' && (
        <label
          data-breeding-blacklist-row="1"
          style={{
            marginTop: 10,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 8,
            maxWidth: '100%',
            boxSizing: 'border-box',
            padding: '6px 12px',
            background: cow.breeding_blacklist ? 'var(--danger-soft)' : 'var(--surface)',
            color: 'var(--danger-ink)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          <input
            type="checkbox"
            checked={!!cow.breeding_blacklist}
            disabled={readOnly}
            onChange={patchOnChange('breeding_blacklist')}
            style={{
              margin: 0,
              padding: 0,
              width: 14,
              height: 14,
              minWidth: 14,
              flex: '0 0 14px',
              boxSizing: 'border-box',
              accentColor: 'var(--danger)',
              cursor: 'pointer',
            }}
          />
          <span style={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block'}}>
            Breeding blacklist
          </span>
        </label>
      )}

      {/* Weight history: the shared ruminant section (table / §4 chart, Table D ADG). */}
      {weighIns && weighIns.length > 0 && <RuminantWeightHistory weighIns={weighIns} fmt={fmt} species="cattle" />}

      {/* Calving history + add (Mommas only) */}
      {isMomma && (
        <div style={{marginTop: 12}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6}}>
            <div style={sectionTitle}>Calving History</div>
            {/* No add handler = the read-only embed (forecast Include Heifers
                modal), so the button is hidden rather than opening a dead form. */}
            {!showCalvingForm && onAddCalving && (
              <Button variant="secondary" type="button" onClick={() => setShowCalvingForm(true)}>
                + Add Calving
              </Button>
            )}
          </div>
          {showCalvingForm && (
            <div
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 12px',
                marginBottom: 8,
              }}
            >
              <div style={{marginBottom: 6}}>
                <div className={recordFieldRowClass}>
                  <span style={recordFieldLabel}>Calving date *</span>
                  <input
                    type="date"
                    value={calvingForm.calving_date}
                    onChange={(e) => setCalvingForm({...calvingForm, calving_date: e.target.value})}
                    style={recordControl}
                  />
                </div>
                <div className={recordFieldRowClass}>
                  <span style={recordFieldLabel}>Calf tag (optional)</span>
                  <input
                    value={calvingForm.calf_tag}
                    onChange={(e) => setCalvingForm({...calvingForm, calf_tag: e.target.value})}
                    placeholder="e.g. 92"
                    style={recordControl}
                  />
                </div>
                <div className={recordFieldRowClass}>
                  <span style={recordFieldLabel}>Sire tag</span>
                  <input
                    value={calvingForm.sire_tag}
                    onChange={(e) => setCalvingForm({...calvingForm, sire_tag: e.target.value})}
                    style={recordControl}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11,
                      color: 'var(--ink)',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={calvingForm.complications_flag}
                      onChange={(e) => setCalvingForm({...calvingForm, complications_flag: e.target.checked})}
                      style={recordCheckbox}
                    />
                    Complications flag
                  </label>
                  {calvingForm.complications_flag && (
                    <textarea
                      value={calvingForm.complications_desc}
                      onChange={(e) => setCalvingForm({...calvingForm, complications_desc: e.target.value})}
                      placeholder="Required: describe complications"
                      rows={2}
                      style={{...recordTextarea, marginTop: 4}}
                    />
                  )}
                </div>
                <div className={recordFieldRowClass} style={{borderBottom: 'none'}}>
                  <span style={recordFieldLabel}>Notes</span>
                  <textarea
                    value={calvingForm.notes}
                    onChange={(e) => setCalvingForm({...calvingForm, notes: e.target.value})}
                    rows={2}
                    style={recordTextarea}
                  />
                </div>
              </div>
              <div style={{display: 'flex', gap: 6}}>
                <Button variant="secondary" type="button" onClick={submitCalving}>
                  Save Calving
                </Button>
                <Button variant="secondary" type="button" onClick={() => setShowCalvingForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {(() => {
            var recordedTags = new Set(
              (calving || [])
                .map(function (r) {
                  return r.calf_tag;
                })
                .filter(Boolean),
            );
            var syntheticFromCalves: CowRecord[] = (calves || [])
              .filter(function (cf) {
                return cf.tag && !recordedTags.has(cf.tag);
              })
              .map(function (cf) {
                return {
                  id: 'synthetic-' + cf.id,
                  synthetic: true,
                  calving_date: cf.birth_date || null,
                  calf_tag: cf.tag,
                  total_born: 1,
                  deaths: 0,
                };
              });
            var combined: CowRecord[] = [...(calving || []), ...syntheticFromCalves].sort(function (
              a: CowRecord,
              b: CowRecord,
            ) {
              return (b.calving_date || '').localeCompare(a.calving_date || '');
            });
            if (combined.length === 0) {
              if (showCalvingForm) return null;
              return (
                <div style={{fontSize: 11, color: 'var(--ink-faint)', fontStyle: 'italic'}}>
                  No calving records yet.
                </div>
              );
            }
            return combined.map(function (r) {
              var calfCow = r.calf_tag ? findByTag(r.calf_tag) : null;
              return (
                <div
                  key={r.id}
                  style={{
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    padding: '6px 10px',
                    marginBottom: 4,
                    fontSize: 11,
                    display: 'flex',
                    gap: 10,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  <strong style={{color: 'var(--ink)'}}>{r.calving_date ? fmt(r.calving_date) : 'date unknown'}</strong>
                  {r.calf_tag &&
                    (calfCow && onNavigateToCow ? (
                      <button
                        type="button"
                        onClick={function () {
                          onNavigateToCow(calfCow as CowRecord);
                        }}
                        style={{
                          color: 'var(--brand)',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                          fontWeight: 600,
                        }}
                      >
                        {'calf #' + r.calf_tag}
                      </button>
                    ) : (
                      <span style={{color: 'var(--text-primary)'}}>{'calf #' + r.calf_tag}</span>
                    ))}
                  {calfCow && (
                    <span style={{fontSize: 10, color: 'var(--ink-muted)'}}>
                      {'\u00b7 ' +
                        (HERD_LABELS[calfCow.herd] || calfCow.herd) +
                        (calfCow.breed ? ' \u00b7 ' + calfCow.breed : '')}
                    </span>
                  )}
                  {r.complications_flag && (
                    <span style={{color: 'var(--danger-ink)', fontWeight: 600}}>{'\u26a0 complications'}</span>
                  )}
                  {r.notes && <span style={{color: 'var(--ink-muted)', fontStyle: 'italic'}}>{r.notes}</span>}
                  {r.synthetic && (
                    <span style={{fontSize: 10, color: 'var(--ink-faint)', fontStyle: 'italic'}}>
                      {'(from calf record)'}
                    </span>
                  )}
                  {!r.synthetic && onDeleteCalving && (
                    <Button
                      variant="danger"
                      type="button"
                      onClick={function () {
                        onDeleteCalving(r.id);
                      }}
                      style={{marginLeft: 'auto'}}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}
      {!isMomma && calving && calving.length > 0 && (
        <div style={{marginTop: 12}}>
          <div style={sectionTitle}>Calving History (Historical)</div>
          {calving.map((r) => (
            <div
              key={r.id}
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '6px 10px',
                marginBottom: 4,
                fontSize: 11,
                display: 'flex',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <strong style={{color: 'var(--ink)'}}>{fmt(r.calving_date)}</strong>
              {r.calf_tag && <span style={{color: 'var(--text-primary)'}}>calf #{r.calf_tag}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Legacy Issues section (hidden on record pages where CommentsSection replaces this) */}
      {!hideComments && (
        <div style={{marginTop: 12}}>
          <div style={sectionTitle}>Issues</div>
          <div style={{display: 'flex', gap: 6, marginBottom: 6}}>
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add an issue..."
              style={{
                flex: 1,
                fontSize: 12,
                padding: '6px 10px',
                border: '1px solid var(--border-strong)',
                borderRadius: 10,
                fontFamily: 'inherit',
              }}
            />
            <Button
              variant="secondary"
              onClick={() => {
                onComment?.(commentText);
                setCommentText('');
              }}
              disabled={!commentText.trim()}
            >
              Add
            </Button>
          </div>
          {comments && comments.length > 0 ? (
            <div style={{maxHeight: 260, overflowY: 'auto'}}>
              {comments.map((c) => {
                const isEditing = editingCommentId === c.id;
                return (
                  <div
                    key={c.id}
                    style={{
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      padding: '6px 10px',
                      marginBottom: 4,
                      fontSize: 11,
                    }}
                  >
                    <div style={{display: 'flex', gap: 8, alignItems: 'center', marginBottom: 2, flexWrap: 'wrap'}}>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '1px 6px',
                          borderRadius: 999,
                          background: 'var(--surface-2)',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {c.source}
                      </span>
                      <span style={{color: 'var(--ink-faint)'}}>{fmt((c.created_at || '').slice(0, 10))}</span>
                      {c.team_member && (
                        <span style={{color: 'var(--ink-faint)', fontWeight: 600}}>{'\u00b7 ' + c.team_member}</span>
                      )}
                      {!isEditing && onEditComment && (
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => {
                            setEditingCommentId(c.id);
                            setEditingCommentText(c.comment || '');
                          }}
                          style={{marginLeft: 'auto'}}
                        >
                          Edit
                        </Button>
                      )}
                      {!isEditing && onDeleteComment && (
                        <Button variant="danger" type="button" onClick={() => onDeleteComment(c.id)}>
                          Delete
                        </Button>
                      )}
                    </div>
                    {isEditing ? (
                      <div>
                        <textarea
                          value={editingCommentText}
                          onChange={(e) => setEditingCommentText(e.target.value)}
                          rows={2}
                          style={{
                            width: '100%',
                            fontSize: 11,
                            padding: '4px 8px',
                            border: '1px solid var(--border-strong)',
                            borderRadius: 10,
                            fontFamily: 'inherit',
                            boxSizing: 'border-box',
                            resize: 'vertical',
                          }}
                        />
                        <div style={{display: 'flex', gap: 6, marginTop: 4}}>
                          <Button
                            variant="secondary"
                            type="button"
                            onClick={() => {
                              onEditComment?.(c.id, editingCommentText);
                              setEditingCommentId(null);
                              setEditingCommentText('');
                            }}
                            disabled={!editingCommentText.trim()}
                          >
                            Save
                          </Button>
                          <Button
                            variant="secondary"
                            type="button"
                            onClick={() => {
                              setEditingCommentId(null);
                              setEditingCommentText('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div style={{color: 'var(--ink)'}}>{c.comment}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{fontSize: 11, color: 'var(--ink-faint)', fontStyle: 'italic'}}>No comments yet.</div>
          )}
          {cow.breeding_blacklist && (
            <div
              style={{
                marginTop: 8,
                background: 'var(--danger-soft)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '6px 10px',
                fontSize: 11,
                display: 'flex',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <Badge variant="danger">Breeding Blacklist</Badge>
              <span style={{color: 'var(--danger-ink)', fontWeight: 600}}>{'Flagged \u2014 do not breed.'}</span>
            </div>
          )}
        </div>
      )}

      {/* Action buttons (Edit removed — fields are inline-editable above) */}
      <div
        style={{
          marginTop: 12,
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          borderTop: '1px solid var(--border)',
          paddingTop: 10,
        }}
      >
        {onDelete && (
          <Button variant="danger" onClick={onDelete} style={{marginLeft: 'auto'}}>
            Delete
          </Button>
        )}
      </div>
    </div>
  );
};

export default CowDetail;
